import React, { useState } from "react";
import { X, FileText } from "lucide-react";
import { noticeApi } from "../services/noticeApi";
import { formatApiErrorDetail } from "../lib/api";
import { useLocalization } from "../hooks/useLocalization";
import { EMPLOYMENT_PERIOD_TYPES } from "../lib/constants";

export const NoticeGenerateDialog = ({ employee, onClose, onGenerated }) => {
  const { t } = useLocalization();
  const [form, setForm] = useState({
    employment_period_type: "INDEFINITE",
    employment_period_start: employee.proposed_start_date || employee.actual_start_date || "",
    workplace: employee.store_location || "",
    job_description: employee.position || "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleGenerate = async () => {
    setError("");
    setSubmitting(true);
    try {
      const { data } = await noticeApi.generate({ employee_id: employee.id, ...form });
      onGenerated(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="generate-notice-dialog">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="text-blue-600" size={20} />
            <h3 className="text-lg font-bold text-gray-900">{t("notice.generate")}</h3>
          </div>
          <button data-testid="close-generate-notice-dialog" onClick={onClose} className="text-gray-500 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="space-y-3">
          <select data-testid="notice-generate-period-type" value={form.employment_period_type} onChange={(e) => setForm((f) => ({ ...f, employment_period_type: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm">
            {EMPLOYMENT_PERIOD_TYPES.map((opt) => <option key={opt} value={opt}>{t(`notice.${opt}`)}</option>)}
          </select>
          <input data-testid="notice-generate-period-start" type="date" value={form.employment_period_start} onChange={(e) => setForm((f) => ({ ...f, employment_period_start: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          <input data-testid="notice-generate-workplace" placeholder={t("notice.workplace")} value={form.workplace} onChange={(e) => setForm((f) => ({ ...f, workplace: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          <input data-testid="notice-generate-job-description" placeholder={t("notice.jobDescription")} value={form.job_description} onChange={(e) => setForm((f) => ({ ...f, job_description: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />

          {error && <p className="text-sm text-red-500" data-testid="generate-notice-error">{error}</p>}

          <button
            data-testid="confirm-generate-notice-button"
            onClick={handleGenerate}
            disabled={submitting || !form.employment_period_start || !form.workplace || !form.job_description}
            className="w-full py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
          >
            {submitting ? t("common.loading") : t("notice.generate")}
          </button>
        </div>
      </div>
    </div>
  );
};
